import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaPowerOff, FaSun, FaMoon } from "react-icons/fa";
import { FiHome, FiZap, FiColumns, FiFileText, FiBarChart2, FiSettings, FiChevronRight, FiChevronDown, FiCalendar, FiAlertTriangle, FiBell, FiLifeBuoy, FiGitMerge, FiUserPlus, FiClipboard, FiCheckSquare, FiActivity } from "react-icons/fi";
import "./SideBar.css";
import { useTheme } from "../context/ThemeContext";

/* ── FixDesk Logo (SVG, themed) ── */
const FixDeskLogo = () => (
    <svg viewBox="0 0 140 28" className="sb-logo-svg" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Circle background */}
        <circle cx="14" cy="14" r="13" fill="rgba(124,92,252,0.1)" />
        {/* Block icon — FD monogram */}
        <rect x="7" y="7" width="6.5" height="6.5" rx="1.5" fill="var(--accent)" opacity="0.35" />
        <rect x="7" y="14.5" width="6.5" height="6.5" rx="1.5" fill="var(--accent)" opacity="0.55" />
        <rect x="14.5" y="7" width="6.5" height="6.5" rx="1.5" fill="var(--accent)" opacity="0.45" />
        <rect x="14.5" y="12" width="6.5" height="9" rx="1.5" fill="var(--accent)" />
        {/* Text: FixDesk — vertically centered with icon */}
        <text x="34" y="18" fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="14" fill="var(--accent)">Fix</text>
        <text x="57" y="18" fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="14" fill="var(--text-primary)">Desk</text>
    </svg>
);

const Sidebar = () => {
    const { mode, toggleTheme } = useTheme();
    const location = useLocation();
    const [expandedSections, setExpandedSections] = useState({});

    let userPermission = [];
    let loginData = null;
    try {
        const stored = localStorage.getItem('userPermissions');
        userPermission = stored ? JSON.parse(stored) : [];
        if (!Array.isArray(userPermission)) userPermission = [];
    } catch {
        userPermission = [];
    }
    try {
        const stored = localStorage.getItem('loginData');
        loginData = stored ? JSON.parse(stored) : null;
    } catch {
        loginData = null;
    }

    // Per-company feature flag (core.Configuration). The QA Time Tracking report
    // is eMedHub-specific, so only companies with the flag enabled see the link.
    let qaTimeTrackingEnabled = false;
    try {
        qaTimeTrackingEnabled = JSON.parse(localStorage.getItem('qaTimeTrackingEnabled')) === true;
    } catch {
        qaTimeTrackingEnabled = false;
    }

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isActive = (path) => location.pathname === path;
    // Prefix match, not equality: a detail route like /whats-new/12 belongs to
    // the same section as /whats-new and should keep it highlighted.
    const isSectionActive = (paths) =>
        paths.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`));

    // Open the section the current route lives in. Without this, landing on a
    // page directly — a bookmark, a redirect, or a link from elsewhere — leaves
    // its section collapsed, so the page you are already looking at appears to
    // be missing from the menu.
    useEffect(() => {
        const sectionFor = [
            ['reports', ['/reports']],
            ['errorTracking', ['/error-tracking']],
            ['customerSupport', ['/customer-support', '/whats-new']],
            ['reminders', ['/reminders']],
            ['masters', ['/MastersTab']],
        ].find(([, paths]) =>
            paths.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
        );
        if (sectionFor) {
            // Only ever opens. Collapsing here would fight the user every time
            // they closed a section while staying on the page.
            setExpandedSections(prev =>
                prev[sectionFor[0]] ? prev : { ...prev, [sectionFor[0]]: true });
        }
    }, [location.pathname]);

    return (
        <div className="sidebar">
            <div className="sb-logo">
                <FixDeskLogo />
            </div>
            <nav className="sb-nav">
                <ul className="sb-menu">
                    {userPermission.includes('Dashboards') && (
                        <li>
                            <Link to="/dashboard" className={`sb-item ${isActive('/dashboard') ? 'sb-item--active' : ''}`}>
                                <FiHome size={14} className="sb-item__icon" />
                                <span>Dashboards</span>
                            </Link>
                        </li>
                    )}

                    <li>
                        <Link to="/my-work" className={`sb-item ${isActive('/my-work') ? 'sb-item--active' : ''}`}>
                            <FiActivity size={14} className="sb-item__icon" />
                            <span>My Work</span>
                        </Link>
                    </li>

                    {userPermission.includes('Client Onboarding') && (
                        <li>
                            <Link to="/Onboarding" className={`sb-item ${isActive('/Onboarding') ? 'sb-item--active' : ''}`}>
                                <FiUserPlus size={14} className="sb-item__icon" />
                                <span>Onboarding</span>
                            </Link>
                        </li>
                    )}

                    {userPermission.includes('Sprints') && (
                        <li>
                            <Link to="/sprints" className={`sb-item ${isActive('/sprints') || isActive('/Sprints') ? 'sb-item--active' : ''}`}>
                                <FiZap size={14} className="sb-item__icon" />
                                <span>Sprints</span>
                            </Link>
                        </li>
                    )}

                    {userPermission.includes('Kanban Chart') && (
                        <li>
                            <Link to="/KanbanBoard" className={`sb-item ${isActive('/KanbanBoard') ? 'sb-item--active' : ''}`}>
                                <FiColumns size={14} className="sb-item__icon" />
                                <span>Kanban Board</span>
                            </Link>
                        </li>
                    )}

                    {userPermission.includes('Sprints') && (
                        <li>
                            <Link to="/gantt" className={`sb-item ${isActive('/gantt') ? 'sb-item--active' : ''}`}>
                                <FiCalendar size={14} className="sb-item__icon" />
                                <span>Gantt Chart</span>
                            </Link>
                        </li>
                    )}

                    {userPermission.includes('Client Requirements') && (
                        <li>
                            <Link to="/ClientRequirements" className={`sb-item ${isActive('/ClientRequirements') || location.pathname.startsWith('/ClientRequirements/') ? 'sb-item--active' : ''}`}>
                                <FiFileText size={14} className="sb-item__icon" />
                                <span>Client Requirements</span>
                            </Link>
                        </li>
                    )}

                    {(userPermission.includes('Request Live Move') || userPermission.includes('Approve Live Move')) && (
                        <li>
                            <Link to="/live-move" className={`sb-item ${isActive('/live-move') ? 'sb-item--active' : ''}`}>
                                <FiGitMerge size={14} className="sb-item__icon" />
                                <span>Live Move</span>
                            </Link>
                        </li>
                    )}

                    {userPermission.includes('Reminders') && (
                        <li>
                            <div
                                className={`sb-item sb-item--parent ${isSectionActive(['/reminders', '/reminders/list', '/reminders/new']) || location.pathname.startsWith('/reminders/') ? 'sb-item--section-active' : ''}`}
                                onClick={() => toggleSection('reminders')}
                            >
                                <FiBell size={14} className="sb-item__icon" />
                                <span>Reminders</span>
                                {expandedSections.reminders ? <FiChevronDown size={12} className="sb-item__chevron" /> : <FiChevronRight size={12} className="sb-item__chevron" />}
                            </div>
                            {expandedSections.reminders && (
                                <ul className="sb-submenu">
                                    <li>
                                        <Link to="/reminders" className={`sb-subitem ${isActive('/reminders') ? 'sb-subitem--active' : ''}`}>
                                            Dashboard
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/reminders/list" className={`sb-subitem ${isActive('/reminders/list') || location.pathname === '/reminders/list' ? 'sb-subitem--active' : ''}`}>
                                            All Reminders
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>
                    )}

                    {userPermission.includes('Reports') && (
                        <li>
                            <div
                                className={`sb-item sb-item--parent ${isSectionActive(['/reports', '/Reports/OverallReports', '/Reports/ClientTATReport', '/Reports/QAFailsReport', '/Reports/BranchAudit', '/Reports/QATimeTracking']) ? 'sb-item--section-active' : ''}`}
                                onClick={() => toggleSection('reports')}
                            >
                                <FiBarChart2 size={14} className="sb-item__icon" />
                                <span>Reports</span>
                                {expandedSections.reports ? <FiChevronDown size={12} className="sb-item__chevron" /> : <FiChevronRight size={12} className="sb-item__chevron" />}
                            </div>
                            {expandedSections.reports && (
                                <ul className="sb-submenu">
                                    <li>
                                        <Link to="/Reports/OverallReports" className={`sb-subitem ${isActive('/Reports/OverallReports') ? 'sb-subitem--active' : ''}`}>
                                            Overall Report
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/Reports/ClientTATReport" className={`sb-subitem ${isActive('/Reports/ClientTATReport') ? 'sb-subitem--active' : ''}`}>
                                            TAT Report
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/Reports/QAFailsReport" className={`sb-subitem ${isActive('/Reports/QAFailsReport') ? 'sb-subitem--active' : ''}`}>
                                            Sprint Performance
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/Reports/BranchAudit" className={`sb-subitem ${isActive('/Reports/BranchAudit') ? 'sb-subitem--active' : ''}`}>
                                            Branch Audit
                                        </Link>
                                    </li>
                                    {qaTimeTrackingEnabled && (
                                        <li>
                                            <Link to="/Reports/QATimeTracking" className={`sb-subitem ${isActive('/Reports/QATimeTracking') ? 'sb-subitem--active' : ''}`}>
                                                QA Time Tracking
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            )}
                        </li>
                    )}

                    {userPermission.includes('Error Tracking') && (
                        <li>
                            <div
                                className={`sb-item sb-item--parent ${isSectionActive(['/error-tracking', '/error-tracking/errors', '/error-tracking/ai-fixes', '/error-tracking/member-report']) ? 'sb-item--section-active' : ''}`}
                                onClick={() => toggleSection('errorTracking')}
                            >
                                <FiAlertTriangle size={14} className="sb-item__icon" />
                                <span>Error Tracking</span>
                                {expandedSections.errorTracking ? <FiChevronDown size={12} className="sb-item__chevron" /> : <FiChevronRight size={12} className="sb-item__chevron" />}
                            </div>
                            {expandedSections.errorTracking && (
                                <ul className="sb-submenu">
                                    <li>
                                        <Link to="/error-tracking" className={`sb-subitem ${isActive('/error-tracking') ? 'sb-subitem--active' : ''}`}>
                                            Dashboard
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/error-tracking/errors" className={`sb-subitem ${isActive('/error-tracking/errors') ? 'sb-subitem--active' : ''}`}>
                                            Error List
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/error-tracking/ai-fixes" className={`sb-subitem ${isActive('/error-tracking/ai-fixes') ? 'sb-subitem--active' : ''}`}>
                                            AI Fixes
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/error-tracking/member-report" className={`sb-subitem ${isActive('/error-tracking/member-report') ? 'sb-subitem--active' : ''}`}>
                                            Member Report
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>
                    )}

                    {userPermission.includes('Customer Support') && (
                        <li>
                            <div
                                className={`sb-item sb-item--parent ${isSectionActive(['/customer-support', '/customer-support/tickets', '/whats-new']) ? 'sb-item--section-active' : ''}`}
                                onClick={() => toggleSection('customerSupport')}
                            >
                                <FiLifeBuoy size={14} className="sb-item__icon" />
                                <span>Customer Support</span>
                                {expandedSections.customerSupport ? <FiChevronDown size={12} className="sb-item__chevron" /> : <FiChevronRight size={12} className="sb-item__chevron" />}
                            </div>
                            {expandedSections.customerSupport && (
                                <ul className="sb-submenu">
                                    <li>
                                        <Link to="/customer-support" className={`sb-subitem ${isActive('/customer-support') ? 'sb-subitem--active' : ''}`}>
                                            Dashboard
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/customer-support/tickets" className={`sb-subitem ${isActive('/customer-support/tickets') ? 'sb-subitem--active' : ''}`}>
                                            Tickets
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/whats-new" className={`sb-subitem ${isActive('/whats-new') ? 'sb-subitem--active' : ''}`}>
                                            What's New
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>
                    )}

                    {userPermission.includes('Masters') && (
                        <li>
                            <div
                                className={`sb-item sb-item--parent ${location.pathname === '/MastersTab' ? 'sb-item--section-active' : ''}`}
                                onClick={() => toggleSection('masters')}
                            >
                                <FiSettings size={14} className="sb-item__icon" />
                                <span>Masters</span>
                                {expandedSections.masters ? <FiChevronDown size={12} className="sb-item__chevron" /> : <FiChevronRight size={12} className="sb-item__chevron" />}
                            </div>
                            {expandedSections.masters && (
                                <ul className="sb-submenu">
                                    <li className="sb-submenu__group">General</li>
                                    <li><Link to="/MastersTab?tab=Projects" className={`sb-subitem ${location.search.includes('tab=Projects') ? 'sb-subitem--active' : ''}`}>Projects</Link></li>
                                    <li><Link to="/MastersTab?tab=Designations" className={`sb-subitem ${location.search.includes('tab=Designations') ? 'sb-subitem--active' : ''}`}>Designations</Link></li>
                                    <li><Link to="/MastersTab?tab=Login+Role" className={`sb-subitem ${location.search.includes('tab=Login') ? 'sb-subitem--active' : ''}`}>Login Role</Link></li>
                                    <li><Link to="/MastersTab?tab=Employees" className={`sb-subitem ${location.search.includes('tab=Employees') ? 'sb-subitem--active' : ''}`}>Employees</Link></li>
                                    <li className="sb-submenu__group">Tickets</li>
                                    <li><Link to="/MastersTab?tab=Ticket+Type" className={`sb-subitem ${location.search.includes('tab=Ticket+Type') ? 'sb-subitem--active' : ''}`}>Ticket Type</Link></li>
                                    <li><Link to="/MastersTab?tab=Priority+Type" className={`sb-subitem ${location.search.includes('tab=Priority') ? 'sb-subitem--active' : ''}`}>Priority Type</Link></li>
                                    <li><Link to="/MastersTab?tab=Ticket+Status" className={`sb-subitem ${location.search.includes('tab=Ticket+Status') ? 'sb-subitem--active' : ''}`}>Ticket Status</Link></li>
                                    <li className="sb-submenu__group">Clients</li>
                                    <li><Link to="/MastersTab?tab=Client" className={`sb-subitem ${location.search === '?tab=Client' ? 'sb-subitem--active' : ''}`}>Client</Link></li>
                                    <li><Link to="/MastersTab?tab=Client+Status" className={`sb-subitem ${location.search.includes('tab=Client+Status') ? 'sb-subitem--active' : ''}`}>Client Status</Link></li>
                                    <li className="sb-submenu__group">Others</li>
                                    <li><Link to="/MastersTab?tab=Impact+Areas" className={`sb-subitem ${location.search.includes('tab=Impact') ? 'sb-subitem--active' : ''}`}>Impact Areas</Link></li>
                                    <li><Link to="/MastersTab?tab=GitLab+Repositories" className={`sb-subitem ${location.search.includes('tab=GitLab') ? 'sb-subitem--active' : ''}`}>GitLab Repos</Link></li>
                                    <li><Link to="/erp-integration" className={`sb-subitem ${location.pathname === '/erp-integration' ? 'sb-subitem--active' : ''}`}>ERP Integration</Link></li>
                                </ul>
                            )}
                        </li>
                    )}

                    {userPermission.includes('Task Assignment') && (
                        <li>
                            <Link to="/task-assignments" className={`sb-item ${isActive('/task-assignments') ? 'sb-item--active' : ''}`}>
                                <FiClipboard size={14} className="sb-item__icon" />
                                <span>Task Assignment</span>
                            </Link>
                        </li>
                    )}

                    <li>
                        <Link to="/my-tasks" className={`sb-item ${isActive('/my-tasks') ? 'sb-item--active' : ''}`}>
                            <FiCheckSquare size={14} className="sb-item__icon" />
                            <span>My Tasks</span>
                        </Link>
                    </li>
                </ul>
            </nav>

            <div className="sb-bottom">
                <p className="sb-username">{loginData?.data?.user?.member?.name || ""}</p>
                <div className="sb-bottom-row">
                    <Link to="/" className="sb-logout-btn">
                        <FaPowerOff size={12} /> Logout
                    </Link>
                    <button
                        className="sb-theme-toggle"
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                        title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {mode === "dark" ? <FaSun size={13} /> : <FaMoon size={13} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
